package com.evoapps.evoshift.photo

import androidx.compose.ui.graphics.ImageBitmap
import com.evoapps.evoshift.domain.repository.ProfilePhotoRepository
import com.evoapps.evoshift.domain.usecase.user.ProfilePhoto
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

/**
 * Profile photos for avatars (SH-94), decoded once and kept in memory (the most recent
 * [CAPACITY]). Photo URLs change with every upload, so a cached URL never shows an old photo.
 * Addresses that failed are not retried in this session (the avatar shows the initials).
 */
class AvatarImages(private val repository: ProfilePhotoRepository) {
    private val images = LinkedHashMap<String, ImageBitmap>()
    private val failed = mutableSetOf<String>()

    fun cached(url: String): ImageBitmap? = images[url]?.also { images.remove(url); images[url] = it }

    /** Remembers [image] as [url]'s (the photo just uploaded, so it is not downloaded again). */
    fun put(url: String, image: ImageBitmap) {
        images.remove(url)
        images[url] = image
        failed -= url
        while (images.size > CAPACITY) images.remove(images.keys.first())
    }

    suspend fun load(url: String): ImageBitmap? {
        cached(url)?.let { return it }
        if (url in failed) return null
        val image = repository.download(url).getOrNull()?.let { bytes ->
            withContext(Dispatchers.Default) { runCatching { decodeImage(bytes, ProfilePhoto.SIDE) }.getOrNull() }
        }
        if (image == null) failed += url else put(url, image)
        return image
    }

    companion object {
        const val CAPACITY = 64
    }
}
